/**
 * LE VOCABULAIRE DES SÛRETÉS — dans `partage`, et c'est la frontière qui l'exige.
 *
 * ⚠️⚠️ CES QUATRE CONSTANTES VIVAIENT DANS `src/domaine/formulaires.ts`, ET
 * L'ÉCRAN LES Y LISAIT. Le build du conteneur a refusé, à juste titre :
 * l'étape `interface` du Dockerfile ne reçoit QUE `src/partage` —
 *
 *     COPY src/partage /app/src/partage
 *     RUN npm run build
 *
 * — si bien qu'un import remontant vers `src/domaine` résout sur le disque du
 * développeur et n'existe pas dans l'étape qui bâtit. Le commentaire du
 * Dockerfile le dit depuis le 09-09, mot pour mot : « un import qui remonte
 * d'un cran compile en local et casse au déploiement ».
 *
 * ⚠️ AUCUN CONTRÔLE LOCAL NE POUVAIT LE VOIR, et c'est le point à retenir.
 * `npx tsc -b` dans `frontend/` et `tsc --noEmit -p frontend/tsconfig.json`
 * rendent tous deux 0 : le fichier EST là, sur le disque. Le défaut n'est ni
 * dans le code ni dans le typage — il est dans la FRONTIÈRE D'UNE ÉTAPE DE
 * BUILD, que seul le conteneur matérialise. `tests/interface-ne-lit-que-partage.test.ts`
 * la matérialise désormais en local, par une lecture de fichiers.
 *
 * ⚠️ ET LA RÈGLE N'EST PAS « déplacer ce qui casse » : c'est que l'interface ne
 * dépend QUE de ce qui est PARTAGÉ. Mesuré au moment du correctif : 21 des 22
 * imports de `frontend/src` vers `src/` visaient déjà `partage` ; le mien était
 * le seul à viser `domaine`. La convention était unanime, je l'ai rompue une
 * fois — et rien ne la faisait respecter.
 *
 * ⚠️ `formulaires.ts` LES RÉ-EXPORTE, il n'en garde pas une seconde copie —
 * même geste que `PIECES_IDENTITE`, et pour la même raison : deux listes égales
 * aujourd'hui divergent au premier ajout fait d'un seul côté.
 */

/**
 * LES SÛRETÉS QU'UNE MAINLEVÉE PEUT LEVER — les trois de `fon.nature_droit`.
 *
 * ⚠️ C'est la garde `fon.garde_mainlevee` (migration 172) qui refuse en base
 * tout ce qui sort de ces trois-là : un menu plus large rendrait une erreur SQL
 * brute à l'agent, sur un choix qu'il vient de faire dans une liste déroulante.
 */
export const NATURES_SURETE = ['hypotheque', 'saisie', 'prenotation'] as const;
export type NatureSurete = (typeof NATURES_SURETE)[number];

export const LIBELLE_SURETE: Record<NatureSurete, string> = {
  hypotheque: 'Hypothèque', saisie: 'Saisie', prenotation: 'Prénotation',
};

/**
 * POURQUOI LA SÛRETÉ TOMBE.
 *
 * ⚠️ LA CAUSE EST OBLIGATOIRE, ET CE N'EST PAS DE L'ARCHIVAGE. Un tiers qui
 * lira le registre dans vingt ans doit savoir si la dette a été payée, remise,
 * prescrite, ou si l'inscription était nulle : les quatre se plaident
 * différemment, et une radiation sans cause se conteste.
 */
export const CAUSES_EXTINCTION = [
  'paiement_integral', 'remise_de_dette', 'nullite_de_l_inscription',
  'peremption', 'decision_de_justice', 'autre',
] as const;
export type CauseExtinction = (typeof CAUSES_EXTINCTION)[number];

export const LIBELLE_CAUSE_EXTINCTION: Record<CauseExtinction, string> = {
  paiement_integral: 'Paiement intégral de la dette',
  remise_de_dette: 'Remise de dette par le créancier',
  nullite_de_l_inscription: "Nullité de l'inscription",
  peremption: "Péremption de l'inscription",
  decision_de_justice: 'Décision de justice',
  autre: 'Autre cause — à préciser en toutes lettres',
};
